/**
 * Ritual Group Chat Service
 * API calls for ritual organizer group chat conversations and messages
 */

import { apiClient } from "@/lib/api-client"
import type {
  RitualGroupConversation,
  RitualGroupConversationListResponse,
  RitualGroupMessage,
  RitualGroupMessagesResponse,
} from "../types/ritual-group-chat.types"

interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export async function createGroupChat(ritualId: string): Promise<RitualGroupConversation> {
  const response = await apiClient.post<ApiResponse<RitualGroupConversation>>(
    "/api/ritual-group-chats",
    { ritualId },
  )
  return response.data.data
}

/**
 * Create or get the per-booking expert group chat. Replaces the older
 * `/api/conversations/expert-chat` (DM) flow.
 */
export async function startExpertGroupChat(opts: {
  bookingId?: string
  ritualId?: string
}): Promise<RitualGroupConversation> {
  const response = await apiClient.post<ApiResponse<RitualGroupConversation>>(
    "/api/ritual-group-chats/expert",
    opts,
  )
  return response.data.data
}

export async function getGroupChat(id: string): Promise<RitualGroupConversation> {
  const response = await apiClient.get<ApiResponse<RitualGroupConversation>>(
    `/api/ritual-group-chats/${id}`,
  )
  return response.data.data
}

export async function getOrganizerChatByRitualId(
  ritualId: string,
): Promise<RitualGroupConversation | null> {
  const response = await apiClient.get<ApiResponse<RitualGroupConversation | null>>(
    `/api/ritual-group-chats/ritual/${ritualId}`,
  )
  return response.data.data
}

export async function listGroupChats(
  page = 1,
  limit = 20,
): Promise<RitualGroupConversationListResponse> {
  const response = await apiClient.get<ApiResponse<RitualGroupConversationListResponse>>(
    "/api/ritual-group-chats",
    { params: { page, limit } },
  )
  return response.data.data
}

export async function getMessages(
  groupChatId: string,
  page = 1,
  limit = 50,
): Promise<RitualGroupMessagesResponse> {
  const response = await apiClient.get<ApiResponse<RitualGroupMessagesResponse>>(
    `/api/ritual-group-chats/${groupChatId}/messages`,
    { params: { page, limit } },
  )
  return response.data.data
}

export async function sendMessage(
  groupChatId: string,
  content: string,
): Promise<RitualGroupMessage> {
  const response = await apiClient.post<ApiResponse<RitualGroupMessage>>(
    `/api/ritual-group-chats/${groupChatId}/messages`,
    { content },
  )
  return response.data.data
}

export async function editMessage(
  groupChatId: string,
  messageId: string,
  content: string,
): Promise<RitualGroupMessage> {
  const response = await apiClient.patch<ApiResponse<RitualGroupMessage>>(
    `/api/ritual-group-chats/${groupChatId}/messages/${messageId}`,
    { content },
  )
  return response.data.data
}

export async function deleteMessage(groupChatId: string, messageId: string): Promise<void> {
  await apiClient.delete(`/api/ritual-group-chats/${groupChatId}/messages/${messageId}`)
}

export async function markAsRead(groupChatId: string): Promise<void> {
  await apiClient.post(`/api/ritual-group-chats/${groupChatId}/read`)
}
